/**
 * Store Locator — trust page (phase 1)
 * docs/superpowers/specs/2026-09-07-store-locator-redesign-design.md
 *
 * Pure helpers live at the top and are exported for Node (`module.exports`)
 * so tests/store-locator-helpers.test.js can run them without a DOM. The
 * DOM controller below consumes the same functions — no duplicate logic.
 */

var PLACEHOLDER_STRINGS = ['check website for hours', 'contact for details'];
var TAG_CAP = 3;
var EARTH_RADIUS_MILES = 3958.8;

/* Literal abbreviation map — slice(0,2) collided (Nebraska/Nevada -> "NE",
 * Puerto Rico/Utah -> "PU"/"U-", British Columbia/... -> "BR"/"CO"). Covers
 * US states/territories, Canadian provinces, and the Mexican states present
 * in assets/stores-data.json; unmapped names fall back to the full string. */
var STATE_ABBR = {
  alabama: 'AL', alaska: 'AK', arizona: 'AZ', arkansas: 'AR', california: 'CA',
  colorado: 'CO', connecticut: 'CT', delaware: 'DE', florida: 'FL', georgia: 'GA',
  hawaii: 'HI', idaho: 'ID', illinois: 'IL', indiana: 'IN', iowa: 'IA',
  kansas: 'KS', kentucky: 'KY', louisiana: 'LA', maine: 'ME', maryland: 'MD',
  massachusetts: 'MA', michigan: 'MI', minnesota: 'MN', mississippi: 'MS',
  missouri: 'MO', montana: 'MT', nebraska: 'NE', nevada: 'NV',
  'new hampshire': 'NH', 'new jersey': 'NJ', 'new mexico': 'NM', 'new york': 'NY',
  'north carolina': 'NC', 'north dakota': 'ND', ohio: 'OH', oklahoma: 'OK',
  oregon: 'OR', pennsylvania: 'PA', 'rhode island': 'RI', 'south carolina': 'SC',
  'south dakota': 'SD', tennessee: 'TN', texas: 'TX', utah: 'UT', vermont: 'VT',
  virginia: 'VA', washington: 'WA', 'west virginia': 'WV', wisconsin: 'WI',
  wyoming: 'WY', 'district of columbia': 'DC', 'puerto rico': 'PR',
  alberta: 'AB', 'british columbia': 'BC', manitoba: 'MB', 'new brunswick': 'NB',
  'newfoundland and labrador': 'NL', 'northwest territories': 'NT', 'nova scotia': 'NS',
  nunavut: 'NU', ontario: 'ON', 'prince edward island': 'PE', quebec: 'QC',
  saskatchewan: 'SK', yukon: 'YT',
  jalisco: 'JAL', coahuila: 'COAH', 'baja california': 'BC-MX', sonora: 'SON',
  chihuahua: 'CHIH', nuevo_leon: 'NL-MX', 'nuevo leon': 'NL-MX'
};

/** Abbreviates a state/province name; falls back to the full name when unmapped. */
function abbreviateState(state) {
  if (!state) return '';
  var key = String(state).trim().toLowerCase();
  return STATE_ABBR[key] || String(state);
}

function isPlaceholder(value) {
  if (value === null || value === undefined) return true;
  var trimmed = String(value).trim();
  if (!trimmed) return true;
  return PLACEHOLDER_STRINGS.indexOf(trimmed.toLowerCase()) !== -1;
}

/** Returns the display-safe string, or '' when the value is a known placeholder. */
function displayValue(value) {
  return isPlaceholder(value) ? '' : String(value);
}

function toRadians(deg) {
  return (deg * Math.PI) / 180;
}

/** Great-circle distance between two lat/lng points, in miles. */
function haversineMiles(lat1, lng1, lat2, lng2) {
  var dLat = toRadians(lat2 - lat1);
  var dLng = toRadians(lng2 - lng1);
  var a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2)) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
  var c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return EARTH_RADIUS_MILES * c;
}

/** Returns a new array, nearest first, plus a `_distanceMiles` field per entry. */
function sortByDistance(locations, origin) {
  return locations
    .map(function (loc) {
      return {
        location: loc,
        distanceMiles: haversineMiles(origin.lat, origin.lng, loc.lat, loc.lng)
      };
    })
    .sort(function (a, b) {
      return a.distanceMiles - b.distanceMiles;
    });
}

/** Case-insensitive substring match on name/city/state/zip. */
/** Country codes in stores-data.json → searchable names (accent-free and accented). */
var COUNTRY_NAMES = {
  US: 'United States USA America',
  CA: 'Canada',
  MX: 'Mexico México'
};

function searchHaystack(loc) {
  var country = loc.country ? String(loc.country).toUpperCase() : '';
  return [loc.name, loc.city, loc.state, abbreviateState(loc.state), loc.zip, country, COUNTRY_NAMES[country]]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
}

function filterByText(locations, query) {
  var q = (query || '').trim().toLowerCase();
  if (!q) return locations.slice();
  var terms = q.split(/\s+/);
  return locations.filter(function (loc) {
    var hay = searchHaystack(loc);
    return terms.every(function (t) { return hay.indexOf(t) !== -1; });
  });
}

/** Caps a tags array at `max` (default 3), returning { shown, overflow }. */
function capTags(tags, max) {
  var limit = max || TAG_CAP;
  var list = Array.isArray(tags) ? tags.filter(Boolean) : [];
  if (list.length <= limit) return { shown: list, overflow: 0 };
  return { shown: list.slice(0, limit), overflow: list.length - limit };
}

/** `type` defaults to "dealer" when absent/unrecognized; "showroom" is the only other value. */
function normalizeType(location) {
  return location && location.type === 'showroom' ? 'showroom' : 'dealer';
}

var DISMISS_KEY = 'qp-sl-showroom-dismissed';
function readDismissed() {
  try { return typeof sessionStorage !== 'undefined' && sessionStorage.getItem(DISMISS_KEY) === '1'; } catch (e) { return false; }
}
function writeDismissed(val) {
  try { if (typeof sessionStorage !== 'undefined') sessionStorage.setItem(DISMISS_KEY, val ? '1' : '0'); } catch (e) { /* private mode */ }
}

function findShowroom(locations) {
  return (locations || []).find(function (loc) {
    return normalizeType(loc) === 'showroom';
  }) || null;
}

var helpers = {
  isPlaceholder: isPlaceholder,
  displayValue: displayValue,
  haversineMiles: haversineMiles,
  sortByDistance: sortByDistance,
  filterByText: filterByText,
  capTags: capTags,
  normalizeType: normalizeType,
  findShowroom: findShowroom,
  abbreviateState: abbreviateState
};

if (typeof module !== 'undefined' && module.exports) {
  module.exports = helpers;
}

/* ========================================================================
 * DOM controller — browser only. Guarded so requiring this file in Node
 * (for the helper tests) never touches `document`/`window`/`navigator`.
 * ==================================================================== */
if (typeof document !== 'undefined') {
  (function () {
    function escapeHtml(str) {
      return String(str == null ? '' : str).replace(/[&<>"']/g, function (ch) {
        return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch];
      });
    }

    function telHref(phone) {
      var digits = String(phone || '').replace(/[^\d+]/g, '');
      return digits ? 'tel:' + digits : '';
    }


    var TAG_LABELS = {
      demo_rides: 'Demo rides',
      group_rides: 'Group rides',
      test_rides: 'Demo rides',
      assembly: 'Assembly',
      service: 'Service',
      custom_paint: 'Custom paint',
      events: 'Events'
    };

    function tagChip(tag, onCarbon) {
      var label = TAG_LABELS[tag] || tag;
      return '<span class="qp-tag' + (onCarbon ? ' qp-tag--on-carbon' : '') + '">' + escapeHtml(label) + '</span>';
    }

    function StoreLocatorMap(root) {
      this.root = root;
      this.stores = [];
      this.dealers = [];
      this.showroom = null;
      this.showroomDismissed = readDismissed();
      this.filtered = [];
      this.origin = null;
      this.selectedId = null;
      this.map = null;
      this.markers = {};
      this.userMarker = null;
      this.mapExpanded = window.matchMedia('(min-width: 1024px)').matches;

      this.els = {
        search: root.querySelector('[data-search-input]'),
        locateBtn: root.querySelector('[data-locate-btn]'),
        locateLabel: root.querySelector('[data-locate-label]'),
        status: root.querySelector('[data-locate-status]'),
        countLine: root.querySelector('[data-count-line]'),
        showroomHero: root.querySelector('[data-showroom-hero]'),
        showroomRestore: root.querySelector('[data-restore-showroom]'),
        cardsList: root.querySelector('[data-cards-list]'),
        fallback: root.querySelector('[data-fallback-card]'),
        expandBtn: root.querySelector('[data-expand-map]'),
        mapCanvas: root.querySelector('[data-map-canvas]')
      };

      this.init();
    }

    StoreLocatorMap.prototype.init = function () {
      var self = this;
      this.loadStores()
        .then(function () {
          self.dealers = self.stores.filter(function (s) {
            return normalizeType(s) === 'dealer';
          });
          self.showroom = findShowroom(self.stores);
          self.filtered = self.dealers.slice();
          self.renderShowroomHero();
          self.renderCards();
          self.updateCountLine();
          self.initMap();
          self.bindEvents();
        })
        .catch(function (err) {
          console.error('store-locator-map: init failed', err);
        });
    };

    StoreLocatorMap.prototype.loadStores = function () {
      var url = this.root.dataset.storesUrl;
      if (!url) return Promise.reject(new Error('missing data-stores-url'));
      return fetch(url)
        .then(function (res) {
          return res.json();
        })
        .then((function (data) {
          this.stores = Array.isArray(data) ? data : [];
        }).bind(this));
    };

    StoreLocatorMap.prototype.bindEvents = function () {
      var self = this;

      if (this.els.search) {
        // theme-global.js defines a global `debounce(fn, ms)`; fall back to
        // an undebounced handler if it isn't loaded (defensive only).
        var onSearchInput = typeof window.debounce === 'function'
          ? window.debounce(function (value) { self.handleSearch(value); }, 150)
          : function (value) { self.handleSearch(value); };
        this.els.search.addEventListener('input', function (e) {
          onSearchInput(e.target.value);
        });
      }

      if (this.els.locateBtn) {
        this.els.locateBtn.addEventListener('click', function () {
          self.geolocate();
        });
      }

      if (this.els.expandBtn) {
        this.els.expandBtn.addEventListener('click', function () {
          self.toggleExpandMap();
        });
      }
    };

    StoreLocatorMap.prototype.handleSearch = function (query) {
      this.query = query;
      // Distance sort (when an origin is known) happens once, in
      // renderCards, which also stamps `_distanceMiles` for the card's
      // right-hand slot — no need to pre-sort here too.
      this.filtered = filterByText(this.dealers, query);
      this.renderCards();
      this.updateCountLine();
      this.renderMarkers();
    };

    StoreLocatorMap.prototype.geolocate = function () {
      var self = this;
      if (!navigator.geolocation) {
        this.setStatus("We can't see your location. Type a city or ZIP instead.");
        return;
      }

      this.setStatus('LOCATING…');
      if (this.els.locateBtn) this.els.locateBtn.disabled = true;

      navigator.geolocation.getCurrentPosition(
        function (position) {
          self.origin = { lat: position.coords.latitude, lng: position.coords.longitude };
          self.setStatus('');
          if (self.els.locateBtn) self.els.locateBtn.disabled = false;
          self.filtered = filterByText(self.dealers, self.query);
          self.renderUserMarker();
          self.renderCards();
          self.updateCountLine();
          self.renderMarkers();
        },
        function () {
          self.setStatus("We can't see your location. Type a city or ZIP instead.");
          if (self.els.locateBtn) self.els.locateBtn.disabled = false;
        }
      );
    };

    StoreLocatorMap.prototype.setStatus = function (msg) {
      if (this.els.status) this.els.status.textContent = msg;
    };

    StoreLocatorMap.prototype.updateCountLine = function () {
      if (!this.els.countLine) return;
      var n = this.filtered.length;
      var suffix = this.origin ? ' · NEAREST FIRST' : '';
      this.els.countLine.textContent = n + (n === 1 ? ' LOCATION' : ' LOCATIONS') + suffix;
      this.els.countLine.hidden = false;
    };

    StoreLocatorMap.prototype.renderShowroomHero = function () {
      var hero = this.els.showroomHero;
      if (!hero || !this.showroom) return;
      if (this.showroomDismissed) { hero.hidden = true; this.setRestoreVisible(true); return; }
      this.setRestoreVisible(false);
      var s = this.showroom;
      var tags = Array.isArray(s.tags) ? s.tags : [];
      // Hero capabilities read as one quiet mono line, not five boxed chips
      // (PM: "badges can look a little busy", 2026-09-08).
      var tagsHtml = capTags(tags, tags.length).shown.map(function (t) {
        return escapeHtml(TAG_LABELS[t] || t);
      }).join(' &middot; ');
      var hours = displayValue(s.hours);
      var open = displayValue(s.open);
      var eyebrow = 'QUICK PRO SHOWROOM · ' + [s.city, abbreviateState(s.state)].filter(Boolean).join(', ');

      // Liquid already rendered `.store-locator-map__hero-media` (section
      // image setting) inside `hero` — build/replace only the body element
      // here so we never clobber that markup. See liquid ~60-64.
      var body = hero.querySelector('.store-locator-map__hero-body');
      if (!body) {
        body = document.createElement('div');
        hero.appendChild(body);
      }
      body.className = 'store-locator-map__hero-body qp-on-carbon';
      body.innerHTML =
          '<button type="button" class="store-locator-map__hero-dismiss" data-dismiss-showroom aria-label="Hide showroom and focus on shops" title="Hide showroom">&times;</button>' +
          '<span class="store-locator-map__hero-eyebrow">' + escapeHtml(eyebrow.toUpperCase()) + '</span>' +
          '<h2 class="store-locator-map__hero-name">' + escapeHtml(s.name) + '</h2>' +
          '<p class="store-locator-map__hero-line">See the bikes, get fit advice, meet the team. Bring your bike in or watch a build before you order.</p>' +
          '<dl class="qp-build-row-list">' +
            (hours ? '<div class="qp-build-row"><dt class="qp-build-row__label">Visits</dt><dd class="qp-build-row__value">' + escapeHtml(hours) + '</dd></div>' : '') +
            (open ? '<div class="qp-build-row"><dt class="qp-build-row__label">Open</dt><dd class="qp-build-row__value">' + escapeHtml(open) + '</dd></div>' : '') +
            '<div class="qp-build-row"><dt class="qp-build-row__label">Address</dt><dd class="qp-build-row__value">' + escapeHtml([s.address, [s.city, abbreviateState(s.state)].filter(Boolean).join(', ') + (s.zip ? ' ' + s.zip : '')].filter(Boolean).join(', ')) + '</dd></div>' +
          '</dl>' +
          (tagsHtml ? '<p class="store-locator-map__hero-tags">' + tagsHtml + '</p>' : '') +
          '<div class="store-locator-map__hero-actions">' +
            '<a class="qp-cta--boxed store-locator-map__hero-cta" href="' + escapeHtml(s.url || '/pages/showroom') + '">REQUEST A VISIT</a>' +
          '</div>' +
        '</div>';
      hero.hidden = false;
      var self = this;
      var dismissBtn = body.querySelector('[data-dismiss-showroom]');
      if (dismissBtn) {
        dismissBtn.addEventListener('click', function () {
          self.dismissShowroom();
        });
      }
    };

    StoreLocatorMap.prototype.setRestoreVisible = function (visible) {
      var btn = this.els.showroomRestore;
      if (!btn) return;
      btn.hidden = !visible;
      if (visible && !btn._bound) {
        btn._bound = true;
        var self = this;
        btn.addEventListener('click', function () { self.restoreShowroom(); });
      }
    };

    /** Bring the showroom hero and its pin back. */
    StoreLocatorMap.prototype.restoreShowroom = function () {
      this.showroomDismissed = false;
      writeDismissed(false);
      this.renderShowroomHero();
      this.renderMarkers();
      if (this.els.showroomHero) this.els.showroomHero.scrollIntoView({ block: 'nearest' });
    };

    /** Hide the showroom hero and its pin for this session so shops take focus. */
    StoreLocatorMap.prototype.dismissShowroom = function () {
      this.showroomDismissed = true;
      writeDismissed(true);
      if (this.els.showroomHero) this.els.showroomHero.hidden = true;
      if (this.showroom && this.selectedId === this.showroom.id) this.selectedId = null;
      this.renderMarkers();
      this.setRestoreVisible(true);
      if (this.els.showroomRestore) this.els.showroomRestore.focus();
    };

    StoreLocatorMap.prototype.cardHtml = function (loc) {
      var addr = [loc.city, loc.state].filter(Boolean).join(', ');
      var rightSlot = this.origin && typeof loc._distanceMiles === 'number'
        ? loc._distanceMiles.toFixed(1) + ' MI'
        : [abbreviateState(loc.state), loc.country].filter(Boolean).join(' · ');
      var capped = capTags(loc.tags);
      var chips = capped.shown.map(function (t) {
        return tagChip(t, false);
      }).join('');
      if (capped.overflow > 0) chips += '<span class="qp-tag">+' + capped.overflow + '</span>';
      var phone = displayValue(loc.phone);
      var selected = this.selectedId === loc.id;

      return (
        '<button type="button" class="qp-card store-locator-map__card' + (selected ? ' is-selected' : '') + '" data-card-id="' + loc.id + '">' +
          '<div class="store-locator-map__card-name-row">' +
            '<span class="store-locator-map__card-name">' + escapeHtml(loc.name) + '</span>' +
            '<span class="store-locator-map__card-right">' + escapeHtml(rightSlot) + '</span>' +
          '</div>' +
          '<div class="store-locator-map__card-city">' + escapeHtml(addr) + '</div>' +
          (chips ? '<div class="store-locator-map__card-tags">' + chips + '</div>' : '') +
          '<div class="store-locator-map__card-footer">' +
            '<span>' + escapeHtml(loc.address) + '</span>' +
            (phone ? '<a href="' + telHref(phone) + '" class="store-locator-map__card-phone">' + escapeHtml(phone) + '</a>' : '') +
          '</div>' +
        '</button>'
      );
    };

    StoreLocatorMap.prototype.renderCards = function () {
      var self = this;
      var list = this.els.cardsList;
      if (!list) return;

      if (this.origin) {
        var withDistance = sortByDistance(this.filtered, this.origin);
        this.filtered = withDistance.map(function (r) {
          r.location._distanceMiles = r.distanceMiles;
          return r.location;
        });
      }

      if (this.filtered.length === 0) {
        var q = escapeHtml(this.query || '');
        list.innerHTML =
          '<p class="store-locator-map__empty">No locations match &ldquo;' + q + '&rdquo;. Try a city or ZIP.</p>' +
          '<button type="button" class="qp-cta--boxed" data-show-all>SHOW ALL</button>';
        var showAllBtn = list.querySelector('[data-show-all]');
        if (showAllBtn) {
          showAllBtn.addEventListener('click', function () {
            if (self.els.search) self.els.search.value = '';
            self.handleSearch('');
          });
        }
        this.renderFallback();
        return;
      }

      list.innerHTML = this.filtered.map(function (loc) {
        return self.cardHtml(loc);
      }).join('');

      list.querySelectorAll('[data-card-id]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          self.selectStore(parseInt(btn.dataset.cardId, 10));
        });
      });

      this.renderFallback();
    };

    StoreLocatorMap.prototype.renderFallback = function () {
      if (this.els.fallback) this.els.fallback.hidden = false;
    };

    StoreLocatorMap.prototype.selectStore = function (id) {
      this.selectedId = id;
      var loc = this.dealers.concat(this.showroom ? [this.showroom] : []).find(function (s) {
        return s.id === id;
      });
      this.renderCards();
      if (loc && this.map) {
        this.map.panTo([loc.lat, loc.lng]);
      }
      var card = this.els.cardsList && this.els.cardsList.querySelector('[data-card-id="' + id + '"]');
      if (card && card.scrollIntoView) card.scrollIntoView({ block: 'nearest' });
    };

    StoreLocatorMap.prototype.toggleExpandMap = function () {
      this.mapExpanded = !this.mapExpanded;
      var btn = this.els.expandBtn;
      var canvas = this.els.mapCanvas;
      if (btn) {
        btn.setAttribute('aria-expanded', String(this.mapExpanded));
        btn.textContent = this.mapExpanded ? 'CLOSE MAP' : 'EXPAND MAP';
      }
      if (canvas) canvas.classList.toggle('is-expanded', this.mapExpanded);
      if (this.map) {
        var self = this;
        this.map.dragging[this.mapExpanded ? 'enable' : 'disable']();
        setTimeout(function () {
          self.map.invalidateSize();
        }, 0);
      }
    };

    /* ---- Leaflet map ---- */

    StoreLocatorMap.prototype.initMap = function () {
      if (typeof L === 'undefined' || !this.els.mapCanvas) return;

      var defaultLat = parseFloat(this.root.dataset.defaultLat || '39.8283');
      var defaultLng = parseFloat(this.root.dataset.defaultLng || '-98.5795');
      var defaultZoom = parseInt(this.root.dataset.defaultZoom || '4', 10);

      this.map = L.map(this.els.mapCanvas, {
        scrollWheelZoom: false,
        zoomControl: false,
        attributionControl: false,
        dragging: this.mapExpanded
      }).setView([defaultLat, defaultLng], defaultZoom);

      L.control.zoom({ position: 'topright' }).addTo(this.map);
      // License line only (no Leaflet prefix/flag); bottom-left so the mobile
      // EXPAND MAP overlay owns the bottom-right corner.
      L.control.attribution({ prefix: false, position: 'bottomleft' }).addTo(this.map);

      // Round 2 (live preview, 2026-09-07): CARTO Positron requires an API
      // key we don't have and served "API KEY REQUIRED" watermark tiles.
      // Reverted to plain OpenStreetMap raster tiles; the grayscale/contrast
      // filter stays on `.leaflet-tile-pane` in the CSS either way.
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 19,
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
      }).addTo(this.map);

      this.renderMarkers();

      var allPins = this.dealers.concat(this.showroom ? [this.showroom] : []);
      if (allPins.length) {
        var bounds = L.latLngBounds(allPins.map(function (s) {
          return [s.lat, s.lng];
        }));
        this.map.fitBounds(bounds, { padding: [24, 24] });
      }
    };

    StoreLocatorMap.prototype.pinIcon = function (type, state) {
      var isShowroom = type === 'showroom';
      var size = isShowroom ? 16 : 12;
      var fill = isShowroom ? 'var(--qp-accent, #C2F230)' : (state === 'active' ? 'var(--qp-ink,#1C1C1C)' : '#FFFFFF');
      var stroke = state === 'active' && !isShowroom ? '#FFFFFF' : '#1C1C1C';
      var strokeWidth = state === 'hover' || state === 'active' ? 3 : 2;
      var shape = isShowroom
        ? '<rect x="4" y="4" width="16" height="16" fill="' + fill + '" stroke="' + stroke + '" stroke-width="' + strokeWidth + '" transform="rotate(45 12 12)"/>'
        : '<circle cx="12" cy="12" r="6" fill="' + fill + '" stroke="' + stroke + '" stroke-width="' + strokeWidth + '"/>';
      return L.divIcon({
        html: '<svg width="24" height="24" viewBox="0 0 24 24">' + shape + '</svg>',
        className: 'store-locator-map__pin store-locator-map__pin--' + type + (state ? ' is-' + state : ''),
        iconSize: [24, 24],
        iconAnchor: [12, 12]
      });
    };

    StoreLocatorMap.prototype.renderMarkers = function () {
      if (!this.map) return;
      var self = this;
      Object.keys(this.markers).forEach(function (id) {
        self.map.removeLayer(self.markers[id]);
      });
      this.markers = {};

      var visibleIds = {};
      this.filtered.forEach(function (loc) {
        visibleIds[loc.id] = true;
      });

      var all = this.dealers.concat(this.showroom ? [this.showroom] : []);
      all.forEach(function (loc) {
        var type = normalizeType(loc);
        if (type === 'dealer' && !visibleIds[loc.id]) return;
        if (type === 'showroom' && self.showroomDismissed) return;
        var state = self.selectedId === loc.id ? 'active' : null;
        var marker = L.marker([loc.lat, loc.lng], {
          icon: self.pinIcon(type, state),
          title: loc.name
        }).addTo(self.map);

        marker.on('click', function () {
          self.selectStore(loc.id);
        });
        marker.bindTooltip(loc.name, { direction: 'top', className: 'store-locator-map__tooltip' });

        self.markers[loc.id] = marker;
      });
    };

    StoreLocatorMap.prototype.renderUserMarker = function () {
      if (!this.map || !this.origin) return;
      if (this.userMarker) this.map.removeLayer(this.userMarker);
      this.userMarker = L.marker([this.origin.lat, this.origin.lng], {
        icon: L.divIcon({
          html: '<span class="store-locator-map__user-dot"></span>',
          className: 'store-locator-map__user-marker',
          iconSize: [8, 8],
          iconAnchor: [4, 4]
        })
      }).addTo(this.map);
      this.map.panTo([this.origin.lat, this.origin.lng]);
    };

    function boot() {
      document.querySelectorAll('.store-locator-map').forEach(function (root) {
        new StoreLocatorMap(root);
      });
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', boot);
    } else {
      boot();
    }
  })();
}
